// TASK-596: the ONE canonical brief shape + reader, consumed by BOTH brief surfaces —
// the canvas `MeetingPrep`/`BriefPanel` and the legacy `BriefTab`. Before this, each read the
// `meeting.preparation_brief` JSONB row with its own ad-hoc destructuring, so the two drifted
// (agentSummaries were canvas-only; meetingAgenda/actionItems/comments were BriefTab-only;
// clientProfile + portfolioRisk were written by the backend but read by NEITHER).
//
// The persisted shape (source of truth: wizard_brief_routes.generate_brief):
//   briefContent   = { executiveSummary, keyPoints, meetingAgenda, actionItems, recommendations, comments }
//   agentSummaries = { clientProfile, portfolio, portfolioRisk, compliance, complianceScore,
//                      complianceOutcome, complianceOutlook, regulatoryIntelligence }
//   version        = optimistic-lock version (top-level)

/** A brief list field: plain strings, or objects the two readers format differently
 * (recommendations {title,description}, agenda {title,duration,topics}, actions {task,owner,due}). */
export type BriefListItem =
  | string
  | { title?: string; description?: string; task?: string; owner?: string; due?: string; duration?: string; topics?: string[] };

export interface BriefContent {
  executiveSummary?: string;
  keyPoints?: BriefListItem[] | string;
  meetingAgenda?: BriefListItem[] | string;
  actionItems?: BriefListItem[] | string;
  recommendations?: BriefListItem[] | string;
  comments?: string;
}

export interface AgentSummaries {
  clientProfile?: string;
  portfolio?: string;
  portfolioRisk?: string;
  compliance?: string;
  complianceScore?: number | null;
  complianceOutcome?: string;
  complianceOutlook?: string;
  regulatoryIntelligence?: Record<string, unknown>;
}

export interface BriefData {
  briefContent: BriefContent;
  agentSummaries: AgentSummaries;
  version?: number;
}

/** Normalise the raw `getMeetingBrief` response into the canonical shape.
 * Tolerates the legacy case where the payload IS the briefContent (no wrapper key),
 * matching the `raw?.briefContent ?? raw` fallback both readers used before. */
export function selectBrief(raw: unknown): BriefData {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    briefContent: (r.briefContent ?? r) as BriefContent,
    agentSummaries: (r.agentSummaries ?? {}) as AgentSummaries,
    version: typeof r.version === 'number' ? r.version : undefined,
  };
}

/** Flatten a brief list field (strings or {title,description} objects) to display strings.
 * Used by the simple canvas renderer; BriefTab formats the same fields more richly itself. */
export function briefList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        const title = (o.title as string) || '';
        const desc = (o.description as string) || '';
        return [title, desc].filter(Boolean).join(': ');
      }
      return '';
    })
    .filter(Boolean);
}
